import {
  useWSModalCustom,
  useWSSnackbar,
  WSControl,
  WSForm,
  WSInputDate,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { Currency, TransactionType } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";
import { queriesTransactions } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  CategoryPickerField,
  CategoryPickerValue
} from "../components/CategoryPicker";
import { RefundExplainer } from "../components/RefundExplainer";
import { TRANSACTION_TYPE_SELECT_OPTIONS } from "../utils/transactionTypeOptions";

type FormData = {
  amount: number | null;
  type: TransactionType;
  category: CategoryPickerValue;
  name: string;
  date: Date;
  isBusiness: boolean;
  note: string;
};

type Props = WSModalCustomProps<boolean>;

const ModalAddTransaction: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [createTransaction, createMeta] =
    queriesTransactions.createTransaction.useMutation();

  const handleSubmit = async (formData: FormData) => {
    await createTransaction(
      {
        transactionCreate: {
          name: formData.name,
          amount: Number(formData.amount ?? 0),
          currency: Currency.USD,
          type: formData.type,
          date: formData.date,
          isBusiness: formData.isBusiness,
          wsCategory: formData.category.wsCategory || undefined,
          subcategory: formData.category.subcategory || undefined,
          note: formData.note || undefined
        }
      },
      {
        onSuccess: () => {
          props.onClose(true);
          openSnackbar({
            icon: { name: "check" },
            message: "Transaction added"
          });
        }
      }
    );
  };

  return (
    <WSForm<FormData>
      onSubmit={handleSubmit}
      defaultValues={{
        amount: null,
        type: TransactionType.EXPENSE,
        category: {},
        name: "",
        date: new Date(),
        isBusiness: true,
        note: ""
      }}
      validationSchema={Yup.object().shape({
        amount: Yup.number()
          .nullable()
          .required("Required")
          .moreThan(0, "Enter an amount"),
        type: Yup.string().required("Required"),
        name: Yup.string().required("Required").max(100, "Too long"),
        date: Yup.date().required("Required")
      })}
    >
      <WSModal
        title="Add transaction"
        size="S"
        onClose={props.onClose}
        actions={{
          buttons: [
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose(false)
            },
            { label: "Add transaction", loading: createMeta.isLoading }
          ]
        }}
      >
        <WSList gap="L">
          <WSForm.Field
            name="amount"
            label="Amount"
            component={WSInputNumber}
            componentProps={{
              min: 0,
              maxFractionDigits: 2,
              placeholder: "0.00"
            }}
          />
          <WSForm.Field
            name="type"
            label="Type"
            component={WSSelect}
            componentProps={{ options: TRANSACTION_TYPE_SELECT_OPTIONS }}
          />
          <WSForm.Value name="type">
            {type =>
              type === TransactionType.REFUND ? <RefundExplainer /> : null
            }
          </WSForm.Value>
          <WSForm.Field
            name="name"
            label="Description"
            component={WSInputText}
            componentProps={{ placeholder: "e.g. Office supplies" }}
          />
          <WSForm.Field name="date" label="Date" component={WSInputDate} />
          <WSForm.Field
            name="category"
            label="Category (optional)"
            component={CategoryPickerField}
          />
          <WSForm.Field
            name="isBusiness"
            component={WSControl}
            componentProps={{ type: "switch", label: "Business" }}
          />
          <WSForm.Field
            name="note"
            label="Note (optional)"
            component={WSInputTextarea}
          />
          <ErrorAlert context="Bookkeeping" error={createMeta.error} />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalAddTransaction = () =>
  useWSModalCustom(ModalAddTransaction);
